/// <reference lib="webworker" />

import { initializeApp } from 'firebase/app';
import {
  getMessaging,
  isSupported,
  onBackgroundMessage,
} from 'firebase/messaging/sw';
import {
  handleFcmPayload,
  type PrepareForPolicyUpdate,
} from './handleFcmPayload';
import { convertBase64UrlToUint8Array } from '../lib/encoding';
import { captureException } from '../helper/sentry';
import * as qinit from '../qinit';
import { PUSH_TAG } from './constants';

declare const self: ServiceWorkerGlobalScope;

let messagingReady: Promise<boolean> | null = null;

/**
 * Ensure push subscription with userVisibleOnly: false
 *
 * Prevents Chrome from showing "site has been updated in the background" notifications.
 * Must be called before Firebase's getToken() to ensure our subscription config is used.
 *
 * @throws Error when notifications not available, permissions not granted, or subscription fails
 */
export const ensurePushSubscription = async (): Promise<void> => {
  if (!('Notification' in self)) {
    console.warn(`${PUSH_TAG} Notification API not available`);
    return;
  }

  if (Notification.permission !== 'granted') {
    console.warn(
      `${PUSH_TAG} Notification permission ${Notification.permission}`
    );
    return;
  }

  const registration = self.registration;
  const existing = await registration.pushManager.getSubscription();

  if (existing) {
    return;
  }

  const vapidKey =
    qinit.tenant.chromebook.pushNotificationConfig?.vapidKey?.key;
  if (!vapidKey) {
    console.warn(`${PUSH_TAG} VapidKey config not available`);
    return;
  }

  const applicationServerKey = convertBase64UrlToUint8Array(vapidKey);

  await registration.pushManager.subscribe({
    userVisibleOnly: false,
    applicationServerKey,
  });
};

/**
 * Initialises Firebase Messaging in the Service Worker.
 *
 * Gates `getMessaging()` behind `isSupported()` to avoid the async
 * `messaging/indexed-db-unsupported` rejection on environments without a
 * usable IndexedDB. Trade-off: Firebase's `push` listener is registered a
 * few microtasks late; a missed payload is recovered by `reportAndUpdate`
 * on the next user navigation.
 */
export const setupFirebaseMessaging = (
  prepareForPolicyUpdate: PrepareForPolicyUpdate
): void => {
  const firebaseConfig =
    qinit.tenant.chromebook.pushNotificationConfig?.firebase;
  if (!firebaseConfig) {
    console.warn(`${PUSH_TAG} Config not available`);
    messagingReady = Promise.resolve(false);
    return;
  }

  messagingReady = isSupported()
    .then(supported => {
      if (!supported) {
        console.warn(
          `${PUSH_TAG} Push Messaging not supported in this context`
        );
        return false;
      }

      try {
        const firebase = initializeApp(firebaseConfig);
        const messaging = getMessaging(firebase);
        onBackgroundMessage(messaging, payload =>
          handleFcmPayload(payload, prepareForPolicyUpdate)
        );
        return true;
      } catch (error) {
        const err = error as Error;
        console.error(`${PUSH_TAG} Setup failed:`, err.message);
        captureException(err);
        return false;
      }
    })
    .catch((error: Error) => {
      console.warn(`${PUSH_TAG} Push Messaging unavailable:`, error.message);
      return false;
    });
};

/**
 * Whether Firebase Messaging finished initialising in this SW lifecycle.
 * Resolves to `false` until `setupFirebaseMessaging` has been called.
 */
export const isFirebaseMessagingReady = (): Promise<boolean> =>
  messagingReady ?? Promise.resolve(false);
